import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Box, Flex, Spinner, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import requestApi from 'utils/Request';
import { EMAIL_DELIVERY_FOR_MERCHANT } from 'utils/StaticApiEndpoints';
import { EMAIL_DELIVERY_STATUS } from 'utils/StaticData';

/**
 * What happened to the invoice emails sent to this merchant.
 *
 * Separate from the Change history above it because it answers a different
 * question. That log records what someone did to the record; this records
 * whether the invoice actually reached anybody - which is only known minutes
 * after the send, when the receiving mail server says so.
 */

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

const MerchantEmailDeliveryList = ({ merchantId }) => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await requestApi.get(
        `${EMAIL_DELIVERY_FOR_MERCHANT}/${merchantId}`,
      );
      setRows(response?.data?.data || []);
      setError(null);
    } catch (err) {
      // Shown rather than swallowed: an empty list and a failed request look
      // identical otherwise, and "no bounces" is exactly the wrong thing to
      // infer from a request that never answered.
      setError(err?.message || 'Could not load the email delivery history.');
    } finally {
      setIsLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    if (merchantId) fetchRows();
  }, [merchantId, fetchRows]);

  if (isLoading) {
    return (
      <Flex justify="center" py="24px">
        <Spinner size="md" color="primary" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Text fontSize="sm" color="red.500">
        {error}
      </Text>
    );
  }

  if (!rows.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        No invoice emails have been sent to this merchant yet.
      </Text>
    );
  }

  return (
    <Box overflowX="auto">
      {rows.map((row) => {
        const state =
          EMAIL_DELIVERY_STATUS[row.status] ?? EMAIL_DELIVERY_STATUS.sent;
        const failed = row.failedRecipients?.length
          ? row.failedRecipients.join(', ')
          : null;

        return (
          <Flex
            key={row.id}
            justify="space-between"
            align="flex-start"
            gap={4}
            py="12px"
            borderBottom="1px solid"
            borderColor="gray.100"
            flexWrap="wrap"
          >
            <Box minW="200px">
              <Text fontSize="sm" fontWeight="600" color="gray.800">
                {formatDateTime(row.sentAt)}
              </Text>
              {/* The address as it was written when the email went out, not the
                  merchant's current one - a bounce usually means the address was
                  wrong, and showing a corrected one makes the failure look
                  inexplicable. */}
              <Text fontSize="xs" color="gray.500">
                {failed || row.recipients}
              </Text>
            </Box>

            <Box flex="1" minW="240px">
              {row.failureReason && (
                <Text fontSize="sm" color="gray.700">
                  {row.failureReason}
                </Text>
              )}
              {row.eventAt && (
                <Text fontSize="xs" color="gray.500">
                  {state.eventLabel} {formatDateTime(row.eventAt)}
                </Text>
              )}
            </Box>

            <Tooltip label={state.tooltip} hasArrow>
              <Badge
                colorScheme={state.colorScheme}
                borderRadius="6px"
                px="8px"
                py="2px"
                flexShrink={0}
              >
                {state.label}
              </Badge>
            </Tooltip>
          </Flex>
        );
      })}
    </Box>
  );
};

MerchantEmailDeliveryList.propTypes = {
  merchantId: PropTypes.number.isRequired,
};

export default MerchantEmailDeliveryList;
